import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { setTimeUntilNextTicketDrawInfo } from '@/common/lib/time-until-next-ticket-draw-info';
import { type LotteryTicketType } from '@/common/types/lottery';
import { TicketsPlaces } from '@/components/Lottery/LotteryFaq/TicketsPlaces/TicketsPlaces';
import { H1, TextSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { Ticket3DPicture } from '@/components/ui/Ticket3DPicture/Ticket3DPicture';
import { setShowTicketDrawInfo } from '@/redux/reducers/lotteryReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const TicketDrawInfo: FC = () => {
  const showTicketDrawInfo = useAppSelector(
    (state) => state.lottery.showTicketDrawInfo,
  );
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const dispatch = useAppDispatch();
  const ticketImagesData: { kind: LotteryTicketType; className?: string }[] = [
    {
      kind: 'Silver',
      className:
        'h-[37.38%] aspect-square absolute z-[1] right-[12.14%] bottom-[19.85%] rotate-[30deg]',
    },
    {
      kind: 'Gold',
      className:
        'h-[37.38%] aspect-square absolute bottom-[34.34%] left-[8.41%] z-[1] -scale-x-[1]',
    },
    {
      kind: 'Diamond',
      className: 'h-[56.07%] aspect-square relative z-[2]',
    },
  ];

  const handleConfirm = (): void => {
    dispatch(setShowTicketDrawInfo(false));

    if (isChecked) {
      setTimeUntilNextTicketDrawInfo();
    }
  };

  return (
    <AnimatePresence>
      {showTicketDrawInfo && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={css.ticketDrawInfo}
        >
          <H1 weight="extrabold" className="!text-[1.5rem] leading-1.3 mb-1">
            {t('Ticket Draw')}
          </H1>
          <TextSmall className="!leading-1.2">
            {t('Collect tickets to participate')}
          </TextSmall>
          <div className={css.background}>
            {ticketImagesData.map((props) => (
              <Ticket3DPicture key={props.kind} {...props} />
            ))}
            <span className={css.blueEffect}></span>
          </div>
          <p className="text-[1.5rem] font-semibold mt-5  leading-1.3 mb-6 720-h:!mb-2 md-h:mb-4 md-h:mt-2 ">
            {t('Where can I get tickets?')}
          </p>
          <TicketsPlaces />
          <div className="flex items-center justify-center mb-6 md-h:mb-4">
            <Checkbox
              onChange={() => setIsChecked((state) => !state)}
              checked={isChecked}
              variant="grey"
              classNames={{
                label: 'flex items-center gap-2',
              }}
              label={() => (
                <TextSmall weight="regular" className="text-white/40">
                  {t('Do not show this week')}
                </TextSmall>
              )}
            />
          </div>
          <MainButton
            onClick={handleConfirm}
            text={'Ok'}
            className="!w-[calc(100%-80px)] mx-[40px] mb-10 md-h:mb-5 720-h:!mb-4 shrink-0"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { TicketDrawInfo };
